/* eslint-disable */
import React, { PropsWithChildren, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { comparer } from "mobx";

import { GlobalContext } from "./GlobalContext";
import { useLoginStateProvider } from "./LoginStateProvider";
import { User } from "./core/api/contracts/User";
import { Organization } from "./core/api/contracts/Organization";
import CommunitySupportMenu from "./CommunitySupportMenu";
import { AnalyticsPropagator, ApiAnalyticsProxy } from "./analytics/analytics";
import { Spin } from "./core/views/Spin";
import { Status } from "./core/api/useLoginState";

type SupportType = "zendesk" | "discord";

function useSupportChat(status: Status) {
  return useMemo<SupportType>(() => {
    const isEnterpriseUser = status.organizations?.find((organization) => organization.plan.clazz === "EnterprisePlan");

    if (isEnterpriseUser || status.loginReply?.user == null) {
      return "zendesk";
    } else {
      return "discord";
    }
  }, [status]);
}

function ExternalServices(props: PropsWithChildren<{}>) {
  const { status, analyticsStatus } = useLoginStateProvider();
  const { analytics, api } = useContext(GlobalContext);

  const supportType = useSupportChat(status);

  const [user, setUser] = useState<User>();
  const [organization, setOrganization] = useState<Organization>();

  // this needs to run before rendering so we will always have the tracker in place for hooks if relevant.
  // Otherwise we might miss events sent by the UserTrackingEvent component for example.
  useLayoutEffect(() => {
    if (analyticsStatus !== "loading" && analyticsStatus === true && analytics instanceof AnalyticsPropagator) {
      analytics.setTracker(
        new ApiAnalyticsProxy(
          (eventProperties: unknown, eventDetails: unknown) =>
            api.postGlobal("user-events", eventProperties, eventDetails),
          { revision: api._revision }
        )
      );
    }
  }, [analyticsStatus, analytics, api]);

  useEffect(() => {
    const selectedOrganization = status?.selectedOrganization;
    if (selectedOrganization) {
      if (!comparer.structural(selectedOrganization, organization)) {
        setOrganization(selectedOrganization);
      }
    }
  }, [status]);

  useEffect(() => {
    const replyUser = status?.loginReply?.user;
    if (replyUser) {
      if (!comparer.structural(replyUser, user)) {
        setUser(replyUser);
      }
    }
  }, [status]);

  useEffect(() => {
    const envType = process.env.REACT_APP_ENV_TYPE;
    if (user) {
      analytics.identifyUser(user.email, {
        role: user.role,
        name: user.name,
        impostor: user.impostor,
        phone: user.phone,
        company: user.company,
        envType,
      });

      console.log(`setting user ${user.email}`);

      if (supportType === "zendesk") {
        if (window.supportChat && !user.impostor ) {
          window.supportChat.boot({ ...user, revision: api._revision });
        }
        window.supportChat.hide();
      }
    } else {
      window.supportChat.hide();
    }
  }, [user, analytics, api, supportType]);

  useEffect(() => {
    const envType = process.env.REACT_APP_ENV_TYPE;
    if (organization) {
      analytics.organization(organization.id, {
        name: user.company || organization.name,
        plan: organization.plan.displayName,
        envType,
      });
      console.log(`setting org ${organization.displayData.name}`);
    }
  }, [organization, analytics]);

  if (analyticsStatus === "loading") {
    return <Spin />;
  }

  return (
    <>
      {props.children}
      {supportType === "discord" && <CommunitySupportMenu />}
    </>
  );
}

export default ExternalServices;
